import { useState, useEffect, useRef } from 'react';

interface FilterSectionProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
}

export default function FilterSection({ selectedCategory, onCategoryChange }: FilterSectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const categories = [
    { name: 'All Insights', icon: 'ri-global-line', count: 24 },
    { name: 'AI & Automation Trends', icon: 'ri-robot-line', count: 8 },
    { name: 'Digital Marketing Strategies', icon: 'ri-line-chart-line', count: 6 },
    { name: 'Branding & Design', icon: 'ri-palette-line', count: 5 },
    { name: 'Emerging Tech', icon: 'ri-rocket-line', count: 3 },
    { name: 'Hash Adz Updates', icon: 'ri-building-line', count: 2 }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="insights"
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #f97316 0%, transparent 50%), 
                           radial-gradient(circle at 75% 75%, #ef4444 0%, transparent 50%)`
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Explore Our Insights
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Browse our curated collection of articles, news releases, and thought leadership pieces. 
            Use the filters below to find exactly what you're looking for.
          </p>
        </div>

        {/* Filter Categories */}
        <div className={`transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {categories.map((category, index) => (
              <button
                key={category.name}
                onClick={() => onCategoryChange(category.name)}
                className={`group p-6 rounded-2xl border-2 transition-all duration-300 transform hover:scale-105 cursor-pointer ${
                  selectedCategory === category.name
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 border-orange-500 text-white shadow-xl'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-orange-300 hover:shadow-lg'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedCategory === category.name
                      ? 'bg-white/20'
                      : 'bg-gradient-to-r from-orange-100 to-red-100'
                  }`}>
                    <i className={`${category.icon} text-xl ${
                      selectedCategory === category.name ? 'text-white' : 'text-orange-600'
                    }`}></i>
                  </div>
                  <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                    selectedCategory === category.name
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {category.count}
                  </span>
                </div>
                <h3 className="font-semibold text-lg mb-2">{category.name}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm opacity-80">View articles</span>
                  <i className={`ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-300 ${
                    selectedCategory === category.name ? 'text-white' : 'text-orange-600'
                  }`}></i>
                </div>
              </button>
            ))}
          </div>

          {/* Active Filter Display */}
          <div className="flex items-center justify-center gap-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <span className="text-gray-600">Currently viewing:</span>
            <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-full">
              <i className={categories.find(c => c.name === selectedCategory)?.icon || 'ri-global-line'}></i>
              <span className="font-semibold">{selectedCategory}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}